import ExpandMore from '@mui/icons-material/ExpandMore';
import HelpOutline from '@mui/icons-material/HelpOutline';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import Label from '~components/FormFields/v2/Label';
import { AiJobSummaryFinding } from '~hooks/useAiSummaries/models';

// 18px matches the waypoint glyphs in the sites list headers
const severityIconSx = { fontSize: 18, flexShrink: 0 };

/**
 * The backend schema enums finding severity to info | warning, but the blob is
 * LLM-written and parsed defensively on this side, so unknown values fall back
 * to a neutral glyph instead of breaking the row.
 */
const SEVERITY_ICONS: Record<string, React.ReactElement> = {
  info: <InfoOutlined sx={{ ...severityIconSx, color: 'info.main' }} />,
  warning: <WarningAmberRounded sx={{ ...severityIconSx, color: 'warning.main' }} />,
};

const FALLBACK_SEVERITY_ICON = (
  <HelpOutline sx={{ ...severityIconSx, color: 'text.secondary' }} />
);

interface AiSummaryFindingsProps {
  findings: AiJobSummaryFinding[];
}

export function AiSummaryFindings({ findings }: AiSummaryFindingsProps) {
  if (findings.length === 0) {
    return null;
  }

  return (
    <Box>
      <Label sx={{ mb: 0.5, pl: '9px' }}>
        {t('admin.job_geo_debug.ai_summary_findings', { total: findings.length })}
      </Label>
      {findings.map((finding, index) => (
        <Accordion
          key={`${finding.code}-${index}`}
          disableGutters
          sx={{
            boxShadow: 'none',
            '&:before': { display: 'none' },
            border: (theme) => `1px solid ${theme.palette.divider}`,
            '&:not(:first-of-type)': { borderTop: 0 },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              minHeight: 36,
              px: 1,
              '& .MuiAccordionSummary-content': {
                my: 0.5,
                minWidth: 0,
                overflow: 'hidden',
                alignItems: 'center',
                gap: 0.75,
              },
            }}
          >
            <Tooltip title={finding.severity ?? ''}>
              {SEVERITY_ICONS[finding.severity ?? ''] ?? FALLBACK_SEVERITY_ICON}
            </Tooltip>
            <Typography variant="body2" noWrap sx={{ fontFamily: 'monospace', flex: 1 }}>
              {finding.code ?? finding.summary ?? ''}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              pt: 0,
              pb: 1,
              px: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            {finding.summary && (
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                {finding.summary}
              </Typography>
            )}
            {finding.loadIndex !== null && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('admin.job_geo_debug.ai_summary_finding_load', {
                  index: finding.loadIndex + 1,
                })}
              </Typography>
            )}
            {finding.evidence && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontFamily: 'monospace' }}
              >
                {finding.evidence}
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
